import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  Modal,
  Button,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stack,
  Box,
} from "@mui/material";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function CreatePersona({ open, onClose, setOpen }: ModalProps) {
  const [baseTemplate, setBaseTemplate] = useState("blank");
  const [isGenerating, setIsGenerating] = useState(false);
  const [personaName, setPersonaName] = useState("");
  const [roleDesc, setRoleDesc] = useState("");
  const [description, setDescription] = useState("");
  const [sensitivity, setSensitivity] = useState("low");

  const handleGenerateAI = () => {
    setIsGenerating(true);
    // Simulate AI logic
    setTimeout(() => {
      setPersonaName("Ops Manager - UAE Marketplace");
      setRoleDesc("Logistics & Vendor Oversight");
      setDescription(
        "Dedicated persona for UAE region, focusing on cross-border logistics and local vendor performance metrics.",
      );
      setSensitivity("medium");
      setIsGenerating(false);
    }, 1500);
  };

  return (
    <Modal className="modal rejectReason_modal" open={open} onClose={onClose}>
      <div className="modal-dialog take_action_modal">
        <div className="modal-body">
          {/* Header */}
          <div className="modal_title">
            <h2>Create Persona</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Form */}
          <form className="form">
            <div className="gap_p">
              {/* Persona Name */}
              <div className="control_group w_50">
                <label htmlFor="persona-name">
                  Persona Name <span className="required">*</span>
                </label>
                <TextField
                  placeholder="e.g., Marketing Manager"
                  fullWidth
                  hiddenLabel
                  value={personaName}
                  onChange={(e) => setPersonaName(e.target.value)}
                  inputProps={{ id: "persona-name" }}
                />
              </div>

              {/* Role Description */}
              <div className="control_group w_50">
                <label htmlFor="role-description">
                  Role Description <span className="required">*</span>
                </label>
                <TextField
                  placeholder="e.g., Campaign &amp; Content Management"
                  fullWidth
                  hiddenLabel
                  value={roleDesc}
                  onChange={(e) => setRoleDesc(e.target.value)}
                  inputProps={{ id: "role-description" }}
                />
              </div>

              {/* Description */}
              <div className="control_group w_100">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <label htmlFor="persona-description" style={{ margin: 0 }}>Description</label>
                  <Button
                    size="small"
                    startIcon={
                      isGenerating ? (
                        <CircularProgress size={12} />
                      ) : (
                        <AutoFixHighIcon />
                      )
                    }
                    onClick={handleGenerateAI}
                    disabled={isGenerating}
                    sx={{ textTransform: "none", py: 0 }}
                  >
                    ✨ Generate with AI
                  </Button>
                </Box>
                <TextField
                  placeholder="Describe the responsibilities and dashboard focus..."
                  fullWidth
                  hiddenLabel
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  inputProps={{ id: "persona-description" }}
                />
              </div>
              {/* Sensitivity Selector */}
              <div className="control_group w_100">
                <label>Persona Sensitivity</label>
                <ToggleButtonGroup
                  value={sensitivity}
                  exclusive
                  onChange={(_, val) => val && setSensitivity(val)}
                  className="template_toggle"
                  fullWidth
                >
                  <ToggleButton value="low" color="success">
                    🟢 Low (View-only)
                  </ToggleButton>
                  <ToggleButton value="medium" color="warning">
                    🟠 Medium (Ops)
                  </ToggleButton>
                  <ToggleButton value="high" color="error">
                    🔴 High (Admin/Fin)
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
              {/* Icon + Color */}

              <div className="control_group w_50">
                <label htmlFor="persona-icon">Icon</label>
                <TextField select fullWidth hiddenLabel defaultValue="users" inputProps={{ id: "persona-icon" }}>
                  <MenuItem value="users">Users</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="analytics">Analytics</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                </TextField>
              </div>

              <div className="control_group w_50">
                <label htmlFor="persona-color">Color</label>
                <TextField select fullWidth hiddenLabel defaultValue="blue" inputProps={{ id: "persona-color" }}>
                  <MenuItem value="blue">Blue</MenuItem>
                  <MenuItem value="green">Green</MenuItem>
                  <MenuItem value="orange">Orange</MenuItem>
                  <MenuItem value="purple">Purple</MenuItem>
                  <MenuItem value="red">Red</MenuItem>
                </TextField>
              </div>

              {/* Base Template */}
              <div className="control_group w_100">
                <label>Base Template</label>
                <ToggleButtonGroup
                  value={baseTemplate}
                  exclusive
                  onChange={(_, val) => val && setBaseTemplate(val)}
                  className="template_toggle"
                >
                  <ToggleButton value="blank">Blank</ToggleButton>
                  <ToggleButton value="admin">Admin</ToggleButton>
                  <ToggleButton value="operations">Operations</ToggleButton>
                </ToggleButtonGroup>
              </div>
            </div>

            {/* Footer */}
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                variant="outlined"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>

              <Button
                className="btn btn_primary"
                onClick={() => setOpen(false)}
              >
              Create
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
